import { QueryClient, QueryFunction, QueryCache, MutationCache } from "@tanstack/react-query";
import i18n from "@/i18n";
import { posthog } from "@/lib/posthog";
import { toast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { stripFormDataImages } from "@/lib/strip-image-metadata";
import { SSEEnvelopeParser, SSEStreamError } from "@/lib/sse";
import { gatewayErrorMessage, looksLikeHtmlBody } from "@/lib/errors/gateway";

function trackApiError(params: {
  method: string;
  url: string;
  status: number | null;
  message: string;
  kind: "http_error" | "timeout" | "network";
}): void {
  try {
    posthog.capture("api_error", {
      method: params.method,
      url: params.url,
      status: params.status,
      message: params.message.slice(0, 300),
      kind: params.kind,
    });
  } catch {
    // posthog not initialized — swallow
  }
}

let authToken: string | null = null;

const getLocaleHeader = (): string => i18n.language || "pl";

export const setAuthToken = (token: string | null) => {
  authToken = token;
};

export const getAuthToken = () => authToken;

async function throwIfResNotOk(res: Response, requestMeta?: { method: string; url: string }) {
  if (res.ok) return;
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get("Retry-After") ?? "5");
    const msg = `Zbyt wiele zapytań — spróbuj ponownie za ${Number.isFinite(retryAfter) ? retryAfter : 5}s.`;
    if (requestMeta)
      trackApiError({ ...requestMeta, status: 429, message: msg, kind: "http_error" });
    throw new Error(`429: ${msg}`);
  }
  const raw = (await res.text()) || res.statusText;
  // Default: keep the raw body. JSON error bodies stay intact so downstream
  // parsing (parseApiErrorMessage's `{…}` extraction, `{ error }`/`{ message }`
  // shapes) keeps working — we only rewrite the message when the body is a
  // non-JSON CDN/proxy HTML page, so raw markup never reaches the UI.
  let msg = raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === "string") msg = parsed.message;
    // else: leave `msg = raw` so `{ error: … }` / `{ issues: … }` bodies survive.
  } catch {
    // Body is not JSON. This is almost always a CDN/proxy HTML error page
    // (CloudFront 504, ALB 502/503) — sanitize so raw markup never reaches the UI.
    const contentType = res.headers.get("content-type") ?? "";
    if (looksLikeHtmlBody(raw) || contentType.includes("text/html")) {
      msg = gatewayErrorMessage(res.status);
    } else {
      // Plain-text error — keep a short, safe snippet only.
      msg = raw.trim().slice(0, 200) || res.statusText || gatewayErrorMessage(res.status);
    }
  }
  if (requestMeta && res.status >= 400) {
    trackApiError({ ...requestMeta, status: res.status, message: msg, kind: "http_error" });
  }
  // The leading "NNN: " status prefix is the app-wide error contract: FriendlyErrorMapper
  // (STATUS_PREFIX regex) and the `err.message.startsWith('401:'/'403:'/…)` guards in
  // useAuth / useAgencyClients / dashboard rely on it to classify errors and to clear an
  // invalid token on 401. Display layers strip the prefix, so it never reaches the user.
  throw new Error(`${res.status}: ${msg}`);
}

const DEFAULT_REQUEST_TIMEOUT_MS = 180_000;

const buildSignal = (external?: AbortSignal, timeoutMs?: number): AbortSignal => {
  if (external) return external;
  // AbortSignal.timeout — Chrome 103+, Safari 16+, FF 100+ (covers our supported matrix).
  return AbortSignal.timeout(timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS);
};

export async function apiRequest(
  method: string,
  url: string,
  data?: any,
  opts?: { signal?: AbortSignal; timeoutMs?: number }
): Promise<any> {
  const options: RequestInit = {
    method: method.toUpperCase(),
    ...(data && { body: JSON.stringify(data) }),
  };
  const headers: Record<string, string> = {};

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  headers["X-Locale"] = getLocaleHeader();

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const finalHeaders = {
    ...headers,
    ...((options.headers as Record<string, string>) || {}),
  };

  if (authToken) {
    finalHeaders.Authorization = `Bearer ${authToken}`;
  }

  const effectiveTimeoutMs = opts?.signal
    ? undefined
    : (opts?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS);
  const requestMeta = { method: method.toUpperCase(), url };
  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers: finalHeaders,
      signal: buildSignal(opts?.signal, opts?.timeoutMs),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === "TimeoutError" || err.name === "AbortError")) {
      const seconds = effectiveTimeoutMs ? Math.round(effectiveTimeoutMs / 1000) : null;
      const suffix = seconds ? ` (limit ${seconds}s)` : "";
      const msg = `Serwer nie odpowiedział na czas${suffix}. Spróbuj ponownie — operacja może być długa.`;
      trackApiError({ ...requestMeta, status: null, message: msg, kind: "timeout" });
      throw new Error(`REQUEST_TIMEOUT: ${msg}`);
    }
    const msg = err instanceof Error ? err.message : String(err);
    trackApiError({ ...requestMeta, status: null, message: msg, kind: "network" });
    throw err;
  }

  await throwIfResNotOk(res, requestMeta);
  return parseJsonOrThrow(res, requestMeta);
}

/**
 * POST that consumes a Server-Sent Events response and resolves with the final
 * payload. Used for long AI generations (e.g. VSL Creator): the backend streams
 * heartbeat comments (`: ping`) to keep CloudFront from cutting the connection at
 * its origin-response timeout, then emits a single envelope on the `data:` line:
 *   { type: 'done', payload: <body> }  — success, resolves with `payload`
 *   { type: 'error', status, message } — failure, throws `${status}: ${message}`
 * so the existing error handling (FriendlyErrorMapper / parseApiErrorMessage) applies.
 * Returns the same shape as the equivalent JSON endpoint, so callers stay unchanged.
 */
export async function apiRequestSSE(
  method: string,
  url: string,
  data?: unknown,
  opts?: { signal?: AbortSignal; timeoutMs?: number; onProgress?: (stage: string) => void }
): Promise<any> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Locale": getLocaleHeader(),
    Accept: "text/event-stream",
  };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const effectiveTimeoutMs = opts?.signal
    ? undefined
    : (opts?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS);
  const requestMeta = { method: method.toUpperCase(), url };

  let res: Response;
  try {
    res = await fetch(url, {
      method: method.toUpperCase(),
      headers,
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
      signal: buildSignal(opts?.signal, opts?.timeoutMs),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === "TimeoutError" || err.name === "AbortError")) {
      const seconds = effectiveTimeoutMs ? Math.round(effectiveTimeoutMs / 1000) : null;
      const suffix = seconds ? ` (limit ${seconds}s)` : "";
      const msg = `Serwer nie odpowiedział na czas${suffix}. Spróbuj ponownie — operacja może być długa.`;
      trackApiError({ ...requestMeta, status: null, message: msg, kind: "timeout" });
      throw new Error(`REQUEST_TIMEOUT: ${msg}`);
    }
    const msg = err instanceof Error ? err.message : String(err);
    trackApiError({ ...requestMeta, status: null, message: msg, kind: "network" });
    throw err;
  }

  // A CDN/proxy failure before streaming starts comes back as a non-OK HTML
  // response — throwIfResNotOk sanitizes it (no raw markup reaches the UI).
  await throwIfResNotOk(res, requestMeta);
  if (!res.body) throw new Error("Brak strumienia odpowiedzi z serwera. Spróbuj ponownie.");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SSEEnvelopeParser(opts?.onProgress);

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
      if (parser.isDone) break;
    }
  } catch (err) {
    if (err instanceof SSEStreamError) {
      trackApiError({
        ...requestMeta,
        status: err.status,
        message: err.detail,
        kind: "http_error",
      });
    }
    throw err;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // reader already released — ignore
    }
  }

  if (!parser.isDone) {
    const msg = "Połączenie przerwane przed zakończeniem generowania. Spróbuj ponownie.";
    trackApiError({ ...requestMeta, status: null, message: msg, kind: "network" });
    throw new Error(msg);
  }
  return parser.result;
}

async function parseJsonOrThrow(
  res: Response,
  requestMeta: { method: string; url: string }
): Promise<any> {
  // Guard against backend returning HTML/text on success — typically happens
  // when an API route is missing and Express falls through to the SPA
  // index.html catch-all. Without this guard the client throws the cryptic
  // "Unexpected token '<', '<!DOCTYPE'..." which hides the real problem.
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const body = await res.text().catch(() => "");
    const snippet = body.slice(0, 120).replace(/\s+/g, " ").trim();
    const msg = `Backend zwrócił niepoprawną odpowiedź (Content-Type: ${contentType || "brak"}). ${snippet ? `Fragment: ${snippet}…` : ""}`;
    trackApiError({ ...requestMeta, status: res.status, message: msg, kind: "http_error" });
    throw new Error(msg);
  }
  return res.json();
}

export async function uploadFile(url: string, formData: FormData): Promise<unknown> {
  const headers: Record<string, string> = {
    "X-Locale": getLocaleHeader(),
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  // Metadane XMP w PNG/JPEG (np. zrzuty ekranu z macOS) triggerują XSS-rule
  // w WAF na prodzie — strip przed wysyłką, inaczej request nie dojdzie.
  const body = await stripFormDataImages(formData);
  const requestMeta = { method: "POST", url };
  const res = await fetch(url, { method: "POST", headers, body });
  await throwIfResNotOk(res, requestMeta);
  return parseJsonOrThrow(res, requestMeta);
}

/**
 * Uploads a file via XHR with progress reporting.
 * 10-minute timeout fits the longest reasonable scheduler video upload (multer limit
 * is 500 MB at /api/scheduler/upload-media). When the proxy in front of the app
 * (ECS ALB / Cloudflare) cuts the connection earlier than that, the user will get
 * a network error from the proxy — this timeout is just our last-resort guard so a
 * stuck XHR doesn't hang indefinitely.
 */
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export function uploadFileWithProgress(
  url: string,
  formData: FormData,
  onProgress?: (percent: number) => void
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.setRequestHeader("X-Locale", getLocaleHeader());
    if (authToken) xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          resolve(xhr.responseText);
        }
      } else {
        let message = `${xhr.status}: ${xhr.statusText}`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed?.message) message = `${xhr.status}: ${parsed.message}`;
        } catch {
          if (xhr.responseText) message = `${xhr.status}: ${xhr.responseText}`;
        }
        reject(new Error(message));
      }
    };

    xhr.onerror = () =>
      reject(new Error("Network error during upload (sprawdź połączenie / VPN / proxy)"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () =>
      reject(
        new Error(
          `Upload timeout po ${UPLOAD_TIMEOUT_MS / 1000}s — plik za duży albo łącze za wolne`
        )
      );
    void stripFormDataImages(formData).then(
      (body) => xhr.send(body),
      () => xhr.send(formData)
    );
  });
}

/**
 * Direct PUT to a presigned R2/S3 URL. Bypasses our backend entirely — file goes
 * straight from browser to object storage, sidestepping ALB/Cloudflare proxy
 * timeouts that would otherwise kill long uploads.
 *
 * Requires the R2 bucket CORS config to allow PUT from the app origin with the
 * Content-Type header that matches the signed URL.
 */
export function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        let detail = xhr.statusText;
        if (xhr.responseText) {
          // S3/R2 returns XML errors — pull the <Message> tag for readability
          const m = xhr.responseText.match(/<Message>(.+?)<\/Message>/);
          if (m) detail = m[1];
          else if (xhr.responseText.length < 200) detail = xhr.responseText;
        }
        reject(new Error(`R2 PUT ${xhr.status}: ${detail}`));
      }
    };

    xhr.onerror = () =>
      reject(new Error("Network error during R2 upload (CORS? sprawdź konsolę przeglądarki)"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () => reject(new Error(`R2 upload timeout po ${UPLOAD_TIMEOUT_MS / 1000}s`));
    xhr.send(file);
  });
}

/**
 * Smart upload: small files go through the multipart endpoint (handler attaches
 * media metadata to user's account); large files use a presigned PUT directly to
 * R2 to dodge proxy-side timeouts. Threshold matches `PRESIGNED_UPLOAD_THRESHOLD`
 * on the server (25 MB).
 */
const PRESIGN_THRESHOLD_BYTES = 25 * 1024 * 1024;

export async function uploadSchedulerMedia(args: {
  file: File;
  forUserId?: string;
  onProgress?: (percent: number) => void;
}): Promise<{ url: string; mediaType: "image" | "video" }> {
  const { file, forUserId, onProgress } = args;
  const isVideo = file.type.startsWith("video/");

  if (file.size <= PRESIGN_THRESHOLD_BYTES) {
    const formData = new FormData();
    formData.append("file", file);
    if (forUserId) formData.append("forUserId", forUserId);
    const data = (await uploadFileWithProgress(
      "/api/scheduler/upload-media",
      formData,
      onProgress
    )) as { url: string; mediaType: "image" | "video" };
    return data;
  }

  const presign = (await apiRequest("POST", "/api/scheduler/presign-upload", {
    filename: file.name,
    mimeType: file.type,
    forUserId,
  })) as { uploadUrl: string; url: string; mediaType: "image" | "video" };

  await putFileToPresignedUrl(presign.uploadUrl, file, onProgress);

  return { url: presign.url, mediaType: presign.mediaType ?? (isVideo ? "video" : "image") };
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  timeoutMs?: number;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, timeoutMs }) =>
  async ({ queryKey, signal }) => {
    const headers: Record<string, string> = {
      "X-Locale": getLocaleHeader(),
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      headers,
      signal: buildSignal(signal, timeoutMs),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res, { method: "GET", url });
    return await res.json();
  };

interface FriendlyErrorMeta {
  readonly silentError?: boolean;
}

const SILENT_ERROR_NAMES = new Set([
  "TwoFactorRequiredError", // control flow — caller renders the TOTP field, not an error
  "AbortError", // user cancelled or component unmounted
  "TimeoutError", // useAuth handles timeout via clearAuth, not a toast
]);

const showFriendlyErrorToast = (error: unknown, meta: FriendlyErrorMeta | undefined): void => {
  if (meta?.silentError) return;
  if (error instanceof Error && SILENT_ERROR_NAMES.has(error.name)) return;
  const friendly = FriendlyErrorMapper.map(error);
  toast({
    variant: "destructive",
    title: friendly.title,
    description: friendly.description,
  });
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      showFriendlyErrorToast(error, query.meta as FriendlyErrorMeta | undefined);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      showFriendlyErrorToast(error, mutation.meta as FriendlyErrorMeta | undefined);
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
      meta: { silentError: true },
    },
    mutations: {
      retry: false,
    },
  },
});
